import { DropdownActionItem, DropdownActions } from '../../../../../../widgets/dropdown';
import { ComposerMenuHint } from './composerMenuHint';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import CheckedIcon from 'src/shared/material_icons/action/check_circle.svg';
import RemoveIcon from 'src/shared/material_icons/action/delete.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import LinkIcon from 'src/shared/material_icons/content/link.svg';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import RetargetIcon from 'src/shared/material_icons/maps/my_location.svg';
import CopyToIcon from 'src/shared/material_icons/content/copy_all.svg';
import ReplaceInIcon from 'src/shared/material_icons/action/find_replace.svg';
import PreviewImageIcon from 'src/shared/material_icons/image/add_photo_alternate.svg';
import UncheckedIcon from 'src/shared/material_icons/toggle/radio_button_unchecked.svg';
import styled, { css } from 'styled-components';
import { markMatches } from '../../../configurator-components/TreeComponent';
import { ComposerRowActionButton as ActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { ComposerAttributeOptionNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getComposerOptionValidationMessages } from '../helpers/composerAttributeValidation';
import ComposerValidationTooltip from './composerValidationTooltip';

export type ComposerOptionRowAction = 'links' | 'duplicate' | 'copy' | 'replace' | 'image' | 'retarget' | 'remove';

interface ComposerOptionTreeElementProps {
    option: ComposerAttributeOptionNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onMoveUp?: (option: ComposerAttributeOptionNode) => void;
    onMoveDown?: (option: ComposerAttributeOptionNode) => void;
    /** La riga intera si trascina (utente, 2026-10-15): il gesto lo conduce chi ospita le righe (`startPointerReorder`). */
    onRowPointerDown?: (event: React.PointerEvent<HTMLElement>, option: ComposerAttributeOptionNode) => void;
    /** La profondità della riga nell'albero: dice al trascinamento di chi è figlia. */
    level?: number;
    onSetDefault?: (option: ComposerAttributeOptionNode) => void;
    onAction?: (action: ComposerOptionRowAction, option: ComposerAttributeOptionNode) => void;
    onVisibilityClick?: (option: ComposerAttributeOptionNode) => void;
    selected: boolean;
    /** La riga è scelta insieme ad altre: il «…» parla della selezione, e offre anche l'immagine di anteprima. */
    inSelection?: boolean;
    hasLink?: boolean;
    indent?: number;
    highlight?: string;
}

const Row = styled.div<{ $selected: boolean; $indent: number }>`
    position: relative;
    display: flex;
    min-width: 0;
    height: 34px;
    align-items: center;
    gap: 7px;
    margin-left: ${(props) => props.$indent}px;
    padding: 0 6px;
    color: var(--composer-fg, #1e2a38);
    background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'transparent')};
    /* La riga scelta: lavato e filo arancio, come in ogni albero dei due editor (treeRowLook). */
    box-shadow: ${(props) => (props.$selected ? 'inset 0 0 0 1px var(--composer-accent, #e8650a)' : 'none')};
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    outline: none;
    user-select: none;

    ${(props) =>
        props.$indent > 0 &&
        css`
            width: calc(100% - ${props.$indent}px);
        `}

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'var(--composer-hover, #e9eff5)')};
    }

    /* Row commands appear on hover, except the ones marked persistent: default, constraints and a disabled option must read at a glance. */
    > div:last-child > *:not([data-persistent]) {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    /* :focus-within alone would keep them lit after a plain click, since the row itself takes focus. */
    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }

    /* Una riga scelta tiene i suoi comandi accesi: è quella su cui si sta lavorando, e doverli
       richiamare col puntatore sarebbe un passaggio in più su ogni gesto. */
    ${(props) =>
        props.$selected &&
        css`
            > div:last-child > * {
                opacity: 1;
            }
        `}
`;

const Main = styled.div<{ $muted: boolean }>`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 8px;
    opacity: ${(props) => (props.$muted ? 0.5 : 1)};
`;

const Preview = styled.span`
    display: inline-flex;
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: var(--composer-kind-option, #b32d7d);

    img,
    svg {
        width: 100%;
        height: 100%;
        object-fit: contain;
        fill: currentColor;
    }
`;

const Name = styled.span`
    overflow: hidden;
    font-size: 13px;
    /* Peso normale per ogni riga, come il prototipo (utente, 2026-10-15). */
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Actions = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 1px;
`;

const Slot = styled.span`
    display: inline-flex;
    width: 28px;
    height: 28px;
`;

export const ComposerOptionTreeElement: FC<ComposerOptionTreeElementProps> = ({
    option,
    onSelect,
    onDoubleClick,
    selected,
    inSelection = false,
    hasLink,
    indent = 0,
    highlight,
    onMoveUp,
    onMoveDown,
    onRowPointerDown,
    level = 1,
    onSetDefault,
    onAction,
    onVisibilityClick
}) => {
    const validationMessages = getComposerOptionValidationMessages(option);

    return (
        <Row
            $selected={selected}
            $indent={indent}
            data-composer-row={option.id}
            data-reorder-id={option.id}
            onPointerDown={onRowPointerDown ? (event) => onRowPointerDown(event, option) : undefined}
            data-reorder-level={level}
            aria-selected={selected}
            onClick={(event) => onSelect(event, option)}
            onDoubleClick={onDoubleClick ? (event) => onDoubleClick(event, option) : undefined}
            onKeyDown={(event) => {
                if (event.key === 'ArrowUp' && event.altKey) {
                    event.preventDefault();
                    onMoveUp?.(option);
                }
                if (event.key === 'ArrowDown' && event.altKey) {
                    event.preventDefault();
                    onMoveDown?.(option);
                }
            }}
            role='treeitem'
            tabIndex={0}
        >
            <Main $muted={option.enabled === false}>
                <Preview data-reorder-anchor>{option.img ? <img src={option.img} alt='' /> : <ImageIcon />}</Preview>
                <Name title={option.name}>{markMatches(option.name, highlight)}</Name>
                {validationMessages.length > 0 && <ComposerValidationTooltip messages={validationMessages} />}
            </Main>
            <Actions>
                {hasLink ? (
                    <ZTooltip title={T._('This option has constraints', 'Admin')}>
                        <ActionButton
                            $active
                            aria-label={T._('Open constraints', 'Admin')}
                            data-persistent='true'
                            onClick={(event) => {
                                event.stopPropagation();
                                onAction?.('links', option);
                            }}
                        >
                            <LinkIcon />
                        </ActionButton>
                    </ZTooltip>
                ) : (
                    <Slot />
                )}
                <ZTooltip title={option.isDefault ? T._('Default option', 'Admin') : T._('Set as default', 'Admin')}>
                    <ActionButton
                        $active={option.isDefault}
                        aria-label={option.isDefault ? T._('Default option', 'Admin') : T._('Set as default', 'Admin')}
                        data-persistent={option.isDefault ? 'true' : undefined}
                        onClick={(event) => {
                            event.stopPropagation();
                            onSetDefault?.(option);
                        }}
                    >
                        {option.isDefault ? <CheckedIcon /> : <UncheckedIcon />}
                    </ActionButton>
                </ZTooltip>
                <ZTooltip title={option.enabled !== false ? T._('Disable this option', 'Admin') : T._('Enable this option', 'Admin')}>
                    <ActionButton
                        $off={option.enabled === false}
                        aria-label={option.enabled !== false ? T._('Disable this option', 'Admin') : T._('Enable this option', 'Admin')}
                        aria-pressed={option.enabled === false}
                        data-persistent={option.enabled === false ? 'true' : undefined}
                        onClick={(event) => {
                            event.stopPropagation();
                            onVisibilityClick?.(option);
                        }}
                    >
                        {option.enabled !== false ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </ActionButton>
                </ZTooltip>
                <DropdownActions
                    header={
                        <ActionButton type='button' aria-label={T._('More commands', 'Admin')}>
                            <MoreIcon />
                        </ActionButton>
                    }
                    items={[
                        <DropdownActionItem key='duplicate' icon={<DuplicateIcon />} onClick={() => onAction?.('duplicate', option)}>
                            {T._('Clone', 'Admin')}
                        </DropdownActionItem>,
                        // COPIARE IN UN ALTRO ATTRIBUTO DAL «…» (utente, 2026-10-15): l'elenco degli attributi si apre al
                        // posto del menu. L'immagine di anteprima solo per una selezione: quella di un'opzione sta nelle Settings.
                        <ComposerMenuHint key='copy' hint={T._('Add a copy of the options to another attribute', 'Admin')}>
                            <DropdownActionItem key='copy' icon={<CopyToIcon />} onClick={() => onAction?.('copy', option)}>
                                {T._('Copy to…', 'Admin')}
                            </DropdownActionItem>
                        </ComposerMenuHint>,
                        <ComposerMenuHint key='replace' hint={T._('Replace the options of another attribute with these', 'Admin')}>
                            <DropdownActionItem key='replace' icon={<ReplaceInIcon />} onClick={() => onAction?.('replace', option)}>
                                {T._('Replace in…', 'Admin')}
                            </DropdownActionItem>
                        </ComposerMenuHint>,
                        inSelection && (
                            <ComposerMenuHint key='image' hint={T._('Set the preview image of all the selected options', 'Admin')}>
                                <DropdownActionItem key='image' icon={<PreviewImageIcon />} onClick={() => onAction?.('image', option)}>
                                    {T._('Preview image…', 'Admin')}
                                </DropdownActionItem>
                            </ComposerMenuHint>
                        ),
                        <ComposerMenuHint key='retarget' hint={T._('Point the actions at other objects and materials', 'Admin')}>
                            <DropdownActionItem key='retarget' icon={<RetargetIcon />} onClick={() => onAction?.('retarget', option)}>
                                {T._('Retarget', 'Admin')}
                            </DropdownActionItem>
                        </ComposerMenuHint>,
                        <DropdownActionItem key='remove' danger icon={<RemoveIcon />} onClick={() => onAction?.('remove', option)}>
                            {T._('Remove', 'Admin')}
                        </DropdownActionItem>
                    ]}
                    minWidth='260px'
                    orientation='RIGHT'
                />
            </Actions>
        </Row>
    );
};
